import { useState } from "react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { Users } from "lucide-react";
import { submitTeamInterest } from "@/lib/growth.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export function TeamInterestForm() {
  const send = useServerFn(submitTeamInterest);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [useCase, setUseCase] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await send({
        data: {
          email: email.trim(),
          company: company.trim() || null,
          teamSize: teamSize ? Math.max(1, Math.min(100000, Number(teamSize) || 1)) : null,
          useCase: useCase.trim() || null,
        },
      });
      if (!res.ok) {
        toast.error(res.message);
        return;
      }
      setSent(true);
      toast.success("You're on the team list — we'll reach out when collaboration is ready.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't send that");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="soft-panel flex h-full flex-col justify-center p-7">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-primary" />
          <p className="micro-label">Teams — coming soon</p>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Thanks for raising your hand. We'll email you when shared workspaces, invites, and
          role-based access are ready — and we'll ask what your team actually needs before we build
          it.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="soft-panel space-y-4 p-7">
      <div className="flex items-center gap-3">
        <Users className="h-5 w-5 text-primary" />
        <p className="micro-label">Teams — coming soon</p>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        Collaboration isn't built yet, but we'd love to know who's waiting. Tell us what your team
        needs and we'll reach out before we ship it.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="team-email">Work email</Label>
          <Input
            id="team-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="team-company">Company</Label>
          <Input
            id="team-company"
            maxLength={200}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="team-size">Approximate team size</Label>
        <Input
          id="team-size"
          type="number"
          min={1}
          max={100000}
          value={teamSize}
          onChange={(e) => setTeamSize(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="team-use-case">What would team mode need to do for you?</Label>
        <Textarea
          id="team-use-case"
          rows={3}
          maxLength={4000}
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
          placeholder="Shared businesses, manager visibility, private journals, reporting…"
        />
      </div>
      <Button type="submit" disabled={busy} className="w-full sm:w-auto">
        {busy ? "Sending…" : "Notify me about team access"}
      </Button>
    </form>
  );
}
